<template>
  <AppLayout>
    <div class="flex flex-col min-h-full bg-slate-50 dark:bg-slate-950">

      <!-- Sticky top bar -->
      <div class="sticky top-0 z-20 bg-slate-50 dark:bg-slate-950 px-4 pt-4 pb-0">
        <div class="flex items-center justify-between mb-3">
          <div>
            <h1 class="text-xl font-bold text-slate-800 dark:text-white leading-tight">Wealth Statement</h1>
            <p class="text-xs text-slate-400 mt-0.5">As of Today</p>
          </div>
          <button @click="showFilters = !showFilters"
            class="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-xl transition"
            :class="showFilters
              ? 'bg-indigo-500 text-white'
              : 'bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300'">
            <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 01-.659 1.591l-5.432 5.432a2.25 2.25 0 00-.659 1.591v2.927a2.25 2.25 0 01-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 00-.659-1.591L3.659 7.409A2.25 2.25 0 013 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0112 3z"/>
            </svg>
            Filters
          </button>
        </div>

        <div v-if="showFilters"
          class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-4 mb-3">
          <div class="grid grid-cols-2 gap-3">
            <div class="flex flex-col gap-1">
              <label class="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">View</label>
              <select v-model="filters.view"
                class="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                       text-slate-800 dark:text-white rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
                <option value="summary">Summary</option>
                <option value="detailed">Detailed</option>
              </select>
            </div>
            <div class="flex flex-col gap-1">
              <label class="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Currency</label>
              <select v-model="filters.currency"
                class="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                       text-slate-800 dark:text-white rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
                <option value="PKR">PKR</option>
                <option value="USD">USD</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div class="flex-1 px-4 pb-6 pt-3 space-y-4">

        <!-- KPI grid -->
        <div class="grid grid-cols-3 lg:grid-cols-6 gap-3">
          <div v-for="kpi in wealthKPIs" :key="kpi.label"
            class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-3 lg:p-4">
            <p class="text-[9px] lg:text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1">{{ kpi.label }}</p>
            <p class="text-sm lg:text-base font-bold leading-tight" :class="kpi.color">{{ kpi.value }}</p>
            <p v-if="kpi.sub" class="text-[9px] lg:text-[10px] text-slate-400 mt-1">{{ kpi.sub }}</p>
          </div>
        </div>

        <!-- Row: Asset allocation + Net Worth Trend -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">

          <!-- Asset allocation doughnut -->
          <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-4">
            <p class="text-sm font-semibold text-slate-700 dark:text-white mb-4">Asset Allocation</p>
            <div class="flex items-center gap-6">
              <div class="shrink-0">
                <Doughnut :data="allocationData" :options="doughnutOptions" style="width:130px;height:130px" />
              </div>
              <div class="flex-1 space-y-2.5">
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-2">
                    <span class="w-2.5 h-2.5 rounded-full bg-blue-500 shrink-0"></span>
                    <span class="text-xs text-slate-500 dark:text-slate-400">Real Estate</span>
                  </div>
                  <span class="text-xs font-bold text-slate-700 dark:text-slate-300">Rs 2.97Cr</span>
                </div>
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-2">
                    <span class="w-2.5 h-2.5 rounded-full bg-purple-500 shrink-0"></span>
                    <span class="text-xs text-slate-500 dark:text-slate-400">Investments</span>
                  </div>
                  <span class="text-xs font-bold text-slate-700 dark:text-slate-300">Rs 64.5L</span>
                </div>
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-2">
                    <span class="w-2.5 h-2.5 rounded-full bg-indigo-500 shrink-0"></span>
                    <span class="text-xs text-slate-500 dark:text-slate-400">Vehicles</span>
                  </div>
                  <span class="text-xs font-bold text-slate-700 dark:text-slate-300">Rs 77L</span>
                </div>
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-2">
                    <span class="w-2.5 h-2.5 rounded-full bg-emerald-500 shrink-0"></span>
                    <span class="text-xs text-slate-500 dark:text-slate-400">Cash & Bank</span>
                  </div>
                  <span class="text-xs font-bold text-slate-700 dark:text-slate-300">Rs 16.5L</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Liabilities breakdown -->
          <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-4">
            <p class="text-sm font-semibold text-slate-700 dark:text-white mb-4">Liabilities Breakdown</p>
            <div class="flex items-center gap-6">
              <div class="shrink-0">
                <Doughnut :data="liabilitiesData" :options="doughnutOptions" style="width:130px;height:130px" />
              </div>
              <div class="flex-1 space-y-2.5">
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-2">
                    <span class="w-2.5 h-2.5 rounded-full bg-red-500 shrink-0"></span>
                    <span class="text-xs text-slate-500 dark:text-slate-400">Home Mortgage</span>
                  </div>
                  <span class="text-xs font-bold text-red-600 dark:text-red-400">Rs 55L</span>
                </div>
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-2">
                    <span class="w-2.5 h-2.5 rounded-full bg-orange-400 shrink-0"></span>
                    <span class="text-xs text-slate-500 dark:text-slate-400">Car Loan</span>
                  </div>
                  <span class="text-xs font-bold text-orange-500">Rs 12L</span>
                </div>
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-2">
                    <span class="w-2.5 h-2.5 rounded-full bg-amber-400 shrink-0"></span>
                    <span class="text-xs text-slate-500 dark:text-slate-400">Business Loan</span>
                  </div>
                  <span class="text-xs font-bold text-amber-500">Rs 8L</span>
                </div>
              </div>
            </div>
          </div>

        </div>

        <!-- Row: Top Assets + Recent Activity -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">

          <!-- Top 5 Assets by Value -->
          <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-4">
            <div class="flex items-center justify-between mb-4">
              <p class="text-sm font-semibold text-slate-700 dark:text-white">Top 5 Assets by Value</p>
              <span class="text-[10px] font-semibold bg-emerald-50 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400 px-2 py-0.5 rounded-full">Highest value</span>
            </div>
            <div class="space-y-3">
              <div v-for="asset in topAssets" :key="asset.name" class="flex items-center gap-3">
                <div class="w-7 h-7 rounded-xl bg-emerald-100 dark:bg-emerald-900 flex items-center justify-center text-[10px] font-bold text-emerald-600 dark:text-emerald-400 shrink-0">
                  {{ asset.name.charAt(0) }}
                </div>
                <div class="flex-1 min-w-0">
                  <div class="flex justify-between mb-1">
                    <span class="text-xs font-medium text-slate-700 dark:text-slate-300 truncate">{{ asset.name }}</span>
                    <span class="text-xs font-bold text-emerald-600 dark:text-emerald-400 ml-2 shrink-0">{{ shortNum(asset.value) }}</span>
                  </div>
                  <div class="h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                    <div class="h-full bg-emerald-500 rounded-full" :style="{ width: asset.pct + '%' }"></div>
                  </div>
                  <p class="text-[10px] text-slate-400 mt-0.5">{{ asset.category }} · {{ asset.ownership }}% owned</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Highest Returns -->
          <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-4">
            <div class="flex items-center justify-between mb-4">
              <p class="text-sm font-semibold text-slate-700 dark:text-white">Top 5 by Returns</p>
              <span class="text-[10px] font-semibold bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 px-2 py-0.5 rounded-full">Best performers</span>
            </div>
            <div class="space-y-3">
              <div v-for="asset in topReturns" :key="asset.name" class="flex items-center gap-3">
                <div class="w-7 h-7 rounded-xl bg-indigo-100 dark:bg-indigo-900 flex items-center justify-center text-[10px] font-bold text-indigo-600 dark:text-indigo-400 shrink-0">
                  {{ asset.name.charAt(0) }}
                </div>
                <div class="flex-1 min-w-0">
                  <div class="flex justify-between mb-1">
                    <span class="text-xs font-medium text-slate-700 dark:text-slate-300 truncate">{{ asset.name }}</span>
                    <span class="text-xs font-bold text-indigo-600 dark:text-indigo-400 ml-2 shrink-0">+{{ asset.return }}%</span>
                  </div>
                  <div class="h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                    <div class="h-full bg-indigo-500 rounded-full" :style="{ width: asset.pct + '%' }"></div>
                  </div>
                  <p class="text-[10px] text-slate-400 mt-0.5">{{ shortNum(asset.value) }} value · {{ asset.years }}y held</p>
                </div>
              </div>
            </div>
          </div>

        </div>

        <!-- All Assets Table -->
        <div>
          <!-- Filter pills -->
          <div class="flex gap-2 mb-3">
            <button @click="assetFilter = 'all'" class="text-xs font-semibold px-4 py-2 rounded-full transition"
              :class="assetFilter === 'all' ? 'bg-indigo-500 text-white' : 'bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400'">
              All Assets
            </button>
            <button @click="assetFilter = 'real_estate'" class="text-xs font-semibold px-4 py-2 rounded-full transition"
              :class="assetFilter === 'real_estate' ? 'bg-blue-500 text-white' : 'bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400'">
              Real Estate
            </button>
            <button @click="assetFilter = 'investments'" class="text-xs font-semibold px-4 py-2 rounded-full transition"
              :class="assetFilter === 'investments' ? 'bg-purple-500 text-white' : 'bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400'">
              Investments
            </button>
          </div>

          <div class="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div v-for="asset in filteredAssets" :key="asset.name"
              class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-4">
              <!-- Header -->
              <div class="flex items-start justify-between mb-3">
                <p class="font-semibold text-slate-800 dark:text-white text-sm leading-snug max-w-[65%]">{{ asset.name }}</p>
                <span class="text-xs font-semibold px-2.5 py-1 rounded-xl shrink-0 ml-2"
                  :class="asset.category === 'Real Estate' ? 'bg-blue-50 dark:bg-blue-950 text-blue-600 dark:text-blue-400'
                    : asset.category === 'Investment'  ? 'bg-purple-50 dark:bg-purple-950 text-purple-600 dark:text-purple-400'
                    : asset.category === 'Vehicle'     ? 'bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400'
                    :                                    'bg-emerald-50 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400'">
                  {{ asset.category }}
                </span>
              </div>
              <!-- Stats grid -->
              <div class="grid grid-cols-4 gap-1.5 text-center">
                <div class="bg-slate-50 dark:bg-slate-800 rounded-xl p-2">
                  <p class="text-[9px] text-slate-400 mb-0.5">Current</p>
                  <p class="text-[11px] font-bold text-slate-700 dark:text-slate-300">{{ shortNum(asset.current) }}</p>
                </div>
                <div class="bg-emerald-50 dark:bg-emerald-950 rounded-xl p-2">
                  <p class="text-[9px] text-slate-400 mb-0.5">Purchased</p>
                  <p class="text-[11px] font-bold text-emerald-600 dark:text-emerald-400">{{ shortNum(asset.purchased) }}</p>
                </div>
                <div :class="asset.return >= 0 ? 'bg-violet-50 dark:bg-violet-950' : 'bg-red-50 dark:bg-red-950'" class="rounded-xl p-2">
                  <p class="text-[9px] text-slate-400 mb-0.5">Return</p>
                  <p class="text-[11px] font-bold" :class="asset.return >= 0 ? 'text-violet-600 dark:text-violet-400' : 'text-red-500 dark:text-red-400'">{{ asset.return >= 0 ? '+' : '' }}{{ asset.return }}%</p>
                </div>
                <div class="bg-indigo-50 dark:bg-indigo-950 rounded-xl p-2">
                  <p class="text-[9px] text-slate-400 mb-0.5">Owned</p>
                  <p class="text-[11px] font-bold text-indigo-600 dark:text-indigo-400">{{ asset.ownership }}%</p>
                </div>
              </div>
              <!-- Footer -->
              <div class="flex items-center justify-between mt-2.5 pt-2 border-t border-slate-50 dark:border-slate-800">
                <p class="text-[10px] text-slate-400">Purchased: <span class="font-semibold text-slate-600 dark:text-slate-300">{{ asset.date }}</span></p>
                <p v-if="asset.location" class="text-[10px] text-slate-400">{{ asset.location }}</p>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  </AppLayout>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { usePage } from '@inertiajs/vue3'
import { Doughnut } from 'vue-chartjs'
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js'
import AppLayout from '@/layouts/AppLayout.vue'

ChartJS.register(ArcElement, Tooltip, Legend)

const page = usePage()
const businessName = computed(() => page.props.auth?.user?.business_name?.trim() || 'Master Ledger')
onMounted(() => { document.title = `Wealth Statement | ${businessName.value}` })

const showFilters = ref(false)
const assetFilter = ref('all')
const filters = ref({ view: 'summary', currency: 'PKR' })

function shortNum(n) {
  n = Number(n) || 0
  if (n >= 10000000) return 'Rs ' + (n / 10000000).toFixed(1) + 'Cr'
  if (n >= 100000)   return 'Rs ' + (n / 100000).toFixed(1) + 'L'
  if (n >= 1000)     return 'Rs ' + (n / 1000).toFixed(1) + 'k'
  return 'Rs ' + n
}

// ── KPIs ─────────────────────────────────────────────
const wealthKPIs = [
  { label: 'Net Worth',       value: 'Rs 3.70Cr', color: 'text-slate-800 dark:text-white',          sub: 'total wealth' },
  { label: 'Total Assets',    value: 'Rs 4.45Cr', color: 'text-emerald-600 dark:text-emerald-400',  sub: '18 assets' },
  { label: 'Liabilities',     value: 'Rs 75L',    color: 'text-red-500 dark:text-red-400',          sub: '3 loans' },
  { label: 'Real Estate',     value: 'Rs 2.97Cr', color: 'text-blue-600 dark:text-blue-400',        sub: '67% of assets' },
  { label: 'Investments',     value: 'Rs 64.5L',  color: 'text-purple-600 dark:text-purple-400',    sub: '14% of assets' },
  { label: 'Avg Return (1Y)', value: '+12.8%',    color: 'text-indigo-600 dark:text-indigo-400',    sub: 'annualized' },
]

// ── Asset allocation doughnut ─────────────────────────
const doughnutOptions = {
  responsive: false,
  plugins: { legend: { display: false } },
  cutout: '70%',
}
const allocationData = {
  labels: ['Real Estate', 'Investments', 'Vehicles', 'Cash & Bank'],
  datasets: [{ data: [29700000, 6450000, 7700000, 1650000], backgroundColor: ['rgba(59,130,246,0.85)', 'rgba(168,85,247,0.85)', 'rgba(99,102,241,0.85)', 'rgba(16,185,129,0.85)'], borderWidth: 0 }],
}

const liabilitiesData = {
  labels: ['Home Mortgage', 'Car Loan', 'Business Loan'],
  datasets: [{ data: [5500000, 1200000, 800000], backgroundColor: ['rgba(239,68,68,0.85)', 'rgba(251,146,60,0.85)', 'rgba(251,191,36,0.85)'], borderWidth: 0 }],
}

// ── Top 5 by value ────────────────────────────────────
const topAssets = [
  { name: 'Main Residence', value: 15000000, category: 'Real Estate', ownership: 100, pct: 100 },
  { name: 'Commercial Plot', value: 8500000, category: 'Real Estate', ownership: 100, pct: 57  },
  { name: 'Rental Property', value: 6200000, category: 'Real Estate', ownership: 100, pct: 41  },
  { name: 'Toyota Corolla',  value: 4500000, category: 'Vehicle',     ownership: 100, pct: 30  },
  { name: 'Honda City',      value: 3200000, category: 'Vehicle',     ownership: 100, pct: 21  },
]

// ── Top 5 by returns ──────────────────────────────────
const topReturns = [
  { name: 'Gold',           value: 950000,  return: 15.8, years: 2,  pct: 100 },
  { name: 'Stock Portfolio', value: 2500000, return: 12.5, years: 3,  pct: 79  },
  { name: 'Govt Bonds',     value: 1200000, return: 11.2, years: 1,  pct: 71  },
  { name: 'Mutual Funds',   value: 1800000, return: 8.3,  years: 2,  pct: 53  },
  { name: 'Commercial Plot', value: 8500000, return: 6.2,  years: 5,  pct: 39  },
]

// ── All assets table ──────────────────────────────────
const allAssets = [
  { name: 'Main Residence',     category: 'Real Estate',   current: 15000000, purchased: 12500000, return: 20,   ownership: 100, date: 'Jan 2020', location: 'DHA Lahore' },
  { name: 'Commercial Plot',    category: 'Real Estate',   current: 8500000,  purchased: 8000000,  return: 6.2,  ownership: 100, date: 'Mar 2019', location: 'Bahria Town' },
  { name: 'Rental Property',    category: 'Real Estate',   current: 6200000,  purchased: 5500000,  return: 12.7, ownership: 100, date: 'Jun 2021', location: 'Gulberg' },
  { name: 'Stock Portfolio',    category: 'Investment',    current: 2500000,  purchased: 2220000,  return: 12.5, ownership: 100, date: 'Jan 2022', location: null },
  { name: 'Mutual Funds',       category: 'Investment',    current: 1800000,  purchased: 1660000,  return: 8.3,  ownership: 100, date: 'May 2023', location: null },
  { name: 'Govt Bonds',         category: 'Investment',    current: 1200000,  purchased: 1080000,  return: 11.2, ownership: 100, date: 'Aug 2024', location: null },
  { name: 'Gold',               category: 'Investment',    current: 950000,   purchased: 820000,   return: 15.8, ownership: 100, date: 'Nov 2022', location: null },
  { name: 'Toyota Corolla',     category: 'Vehicle',       current: 4500000,  purchased: 5200000,  return: -13.5,ownership: 100, date: 'Feb 2022', location: null },
  { name: 'Honda City',         category: 'Vehicle',       current: 3200000,  purchased: 3800000,  return: -15.8,ownership: 100, date: 'Jul 2020', location: null },
  { name: 'HBL Savings',        category: 'Cash & Bank',   current: 850000,   purchased: 850000,   return: 0,    ownership: 100, date: 'Current',  location: null },
  { name: 'Meezan Bank',        category: 'Cash & Bank',   current: 620000,   purchased: 620000,   return: 0,    ownership: 100, date: 'Current',  location: null },
  { name: 'Cash in Hand',       category: 'Cash & Bank',   current: 180000,   purchased: 180000,   return: 0,    ownership: 100, date: 'Current',  location: null },
]

const filteredAssets = computed(() => {
  if (assetFilter.value === 'real_estate') return allAssets.filter(a => a.category === 'Real Estate')
  if (assetFilter.value === 'investments') return allAssets.filter(a => a.category === 'Investment')
  return allAssets
})
</script>
